/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import ButtonLink from '@/components/buttons/ButtonLink'
import ListingSamCard from '@/components/cards/ListingSamCard'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { SAMS_LIST_TITLE } from '@/configs/strings/sams-list'
import { samsBuildBatchesUrls } from '@/configs/urls'
import { getSamsAsCsv, getSams } from '@/services/sams'
import {
  getItemsPropsForTable,
  getSamCardProps,
  tableColumns,
  tableInitialState
} from './handlers'
import ItemsListWithToggleLayout from '@/components/wrappers/general/ItemsListWithToggleLayout'
import { DownloadFileButton } from '@/components/buttons/DownloadFileButton'
import { Box } from '@chakra-ui/react'
import { BUILD_SAM_BATCHES_TITLE } from '@/configs/strings/sams-build-batches.strings'
import DefaultTableHidingColumns from '@/components/tables/DefaultTableHidingColumns'
import ActionBar from './components/ActionBar'

const SamsList = () => {
  const urlCreate = `/${samsBuildBatchesUrls.base}`
  const {
    itemsResponse,
    loading,
    onChangePage,
    page,
    searchParams,
    setSearchParams,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getSams
  })
  const samsCards = itemsResponse?.sams?.map(item => {
    const cardProps = getSamCardProps(item)

    return (
      <ListingSamCard
        key={cardProps.key}
        {...cardProps}
      />
    )
  })

  const table = <DefaultTableHidingColumns
    columns={tableColumns}
    getItemProps={getItemsPropsForTable}
    data={itemsResponse?.sams}
    tableInitialState={tableInitialState}
    visibleColumnsParamKey="visibleColumns"
  />

  const CreateButton = () => (
    <Box
      display="flex"
      justifyContent="space-between"
    >
      <ButtonLink
        to={urlCreate}
      >
        { BUILD_SAM_BATCHES_TITLE }
      </ButtonLink>
      <DownloadFileButton
        service={getSamsAsCsv}
        isDisabled={!itemsResponse?.sams?.length}
        dataKey="csv"
      >
        Export SAMs List
      </DownloadFileButton>
    </Box>
  )

  return (
    <RegularTemplate
      title={SAMS_LIST_TITLE}
      childrenHeader={CreateButton}
    >
      <ItemsListWithToggleLayout
        cards={ samsCards }
        currentPage={page}
        header={
          <ActionBar
            searchParams={searchParams}
            onFilterResults={(filter) => setSearchParams(filter)}
            onReset={() => setSearchParams({})}
          />
        }
        itemsLayout={searchParams.itemsLayout}
        isLoading={loading}
        onChangePage={onChangePage}
        searchParams={searchParams}
        table={table}
        totalItems={itemsResponse?.total}
        totalPages={totalPages}
      />
    </RegularTemplate>
  )
}

export default SamsList
